import React, { useState, useEffect, useRef } from 'react';
import Popup, { ToolbarItem as PopupToolbarItem } from 'devextreme-react/popup';
import { CheckBox } from 'devextreme-react/check-box';
import DataGrid, { Column, Editing, Scrolling, Selection, Sorting, FilterRow, ColumnChooser, Export, Lookup } from 'devextreme-react/data-grid';
import { WMSInventoryUOMFlags } from '../../../../../../const/constants';
import { MESSAGES } from '../../../../../../common/Messages';

// WMD0115 - "Replicate Shipment Item Details Based on Auto Lot Selection".
//
// Opens after the replicated SHIPMENT header is saved, when the WMD0113 Lot choice was
// "Auto Select Lot". The sibling WMD0114 handles the other two lot choices.
//
// The user ticks the lines to ship and may override the suggested quantity; OK creates one
// transaction detail per ticked line and the server then runs the stage change, which allocates the
// lots, sub-lots and locations automatically.

// The memo beside the item list. Delphi words it this way because the stage change is called with
// SUBLOTMETHOD and LOCATIONMETHOD forced to Auto on this path, whatever the profile says - so the
// operator is told their profile settings are not in play here.
const autoLotNote = [
    'Note : You have selected to AUTO select the "Lot" option.',
    'Please Note that "Sublot" and "Location" will be automatically allocated regardless of the settings in the transaction data profile record.',
];

const helpText =
    'The Original Replicate Shipment Transaction has the following items. Please enter the Quantity ' +
    'you wish to ship for each line Item and Click the OK button to create the "Transaction Detail" ' +
    'record(s). The quantity entered in this screen will be defaulted to the Order & Actual Quantity ' +
    'fields in the transaction detail record based on the stage in this transaction.';

const ReplicateShipmentAutoLot = ({ visible, onClose, onOk, items }) => {
    const [rows, setRows] = useState([]);

    // Mirrors `rows`. saveEditData() below writes the open cell back through onRowUpdated, but a
    // setState from that handler is not visible to the same tick - reading state straight after the
    // await would still return the pre-edit value, which is the bug this guards.
    const rowsRef = useRef([]);
    const applyRows = (next) => {
        rowsRef.current = next;
        setRows(next);
    };

    const dataGrid = useRef(null);

    // Reloaded each time the dialog opens, so a previous run's edits are never reused.
    useEffect(() => {
        if (!visible) return;
        applyRows((items || []).map((item, index) => ({ ...item, __key: index })));
    }, [visible, items]);

    const allSelected = rows.length > 0 && rows.every(r => r.selected);

    const toggleAll = (value) => {
        // Delphi's SelectAll also zeroes the ship quantity of anything it unticks, the same as
        // unticking a single row does.
        applyRows(rows.map(r => ({ ...r, selected: value, shipQuantity: value ? r.shipQuantity : 0 })));
    };

    const onRowUpdating = (e) => {
        const merged = { ...e.oldData, ...e.newData };
        // Unticking a line clears its quantity (grdtemDetailsSELECTEDToggleClick).
        if (e.newData.selected === false) merged.shipQuantity = 0;
        e.newData.selected = merged.selected;
        e.newData.shipQuantity = merged.shipQuantity;
    };

    // Delphi disables OK outright while a row is being edited (BeforeEdit / AfterPost), so the edit
    // can never be in flight when OK is pressed. Committing it instead reaches the same outcome -
    // no lost keystroke - without taking the button away mid-type. Same approach as
    // CommonReport.commitPendingGridEdits, which exists for exactly this failure.
    const handleOk = async () => {
        const grid = dataGrid.current;
        if (grid) {
            await grid.instance().saveEditData();
        }

        if (onOk) onOk(rowsRef.current.map(({ __key, ...row }) => row));
    };

    return (
        <Popup
            visible={visible}
            onHiding={onClose}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title="Replicate Shipment Item Details Based on Auto Lot Allocation (WMD0115)"
            container=".dx-viewport"
            width="80%"
            height="70%"
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div
                    style={{
                        border: '1px solid #999',
                        padding: '8px 10px',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        marginBottom: '10px',
                    }}
                >
                    {helpText}
                </div>

                <div
                    style={{
                        border: '1px solid #999',
                        padding: '8px 10px',
                        fontSize: '12px',
                        marginBottom: '10px',
                    }}
                >
                    <div style={{ fontWeight: 'bold' }}>{autoLotNote[0]}</div>
                    <div>{autoLotNote[1]}</div>
                </div>

                <div style={{ flex: 1, minHeight: 0 }}>
                    <DataGrid
                        ref={dataGrid}
                        dataSource={rows}
                        keyExpr="__key"
                        showBorders={true}
                        rowAlternationEnabled={true}
                        allowColumnResizing={true}
                        onExporting={(e) => {
                            if (window.handleDevExtremeDataGridExport) {
                                window.handleDevExtremeDataGridExport(e);
                            }
                        }}
                        columnAutoWidth={true}
                        height="100%"
                        onRowUpdating={onRowUpdating}
                        onRowUpdated={(e) => applyRows(rowsRef.current.map(r => (r.__key === e.key ? { ...r, ...e.data } : r)))}
                    >
                        <Editing mode="cell" allowUpdating={true} />
                        <Scrolling mode="virtual" />
                        <Selection mode="none" />
                        <Sorting mode="multiple" />
                        <FilterRow visible={true} />
                        <ColumnChooser enabled={true} />
                        <Export enabled={true} />

                        <Column dataField="selected" caption="" dataType="boolean" width={50} />
                        <Column dataField="itemCode" caption="Item" allowEditing={false} />
                        <Column dataField="itemDescription1" caption="Item Description - 1" allowEditing={false} />
                        <Column dataField="itemDescription2" caption="Item Desc - 2" allowEditing={false} visible={false} />
                        <Column dataField="itemAvailable" caption="Item Available" dataType="number" alignment="right" allowEditing={false} format={{ type: "fixedPoint", precision: 2 }} />
                        <Column dataField="inventoryUomFlag" caption="Trans Unit" allowEditing={false}>
                            {/* I / P / K / H are stored codes; the operator sees the description. */}
                            <Lookup dataSource={WMSInventoryUOMFlags} displayExpr="Text" valueExpr="ID" />
                        </Column>
                        <Column dataField="totalQuantityOrder" caption="Ordered" dataType="number" alignment="right" allowEditing={false} format={{ type: "fixedPoint", precision: 2 }} />
                        <Column dataField="totalQuantityActual" caption="Actual" dataType="number" alignment="right" allowEditing={false} format={{ type: "fixedPoint", precision: 2 }} />
                        {/* The only editable column, as in Delphi where the cursor tabs straight to it. */}
                        <Column dataField="shipQuantity" caption="Ship Quantity" dataType="number" alignment="right" allowEditing={true} format={{ type: "fixedPoint", precision: 2 }} />
                    </DataGrid>
                </div>

                <div style={{ paddingTop: '8px' }}>
                    <CheckBox
                        value={allSelected}
                        text={allSelected ? 'Unselect All' : 'Select All'}
                        onValueChanged={(e) => toggleAll(e.value)}
                    />
                </div>
            </div>

            <PopupToolbarItem
                toolbar="top"
                location="after"
                widget="dxButton"
                options={{
                    text: MESSAGES.OK ?? 'OK',
                    type: 'default',
                    stylingMode: 'contained',
                    elementAttr: { class: 'accordianButton' },
                    onClick: handleOk,
                }}
            />
        </Popup>
    );
};

export default ReplicateShipmentAutoLot;
